import { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import { Input } from "../ui/input";
import { Textarea } from "../ui/textarea";
import { http, formatApiErrorDetail } from "../../lib/auth";

const EMPTY = { name: "", price: "", period: "per month", description: "", perks: "", join_url: "", order: 0 };

const toForm = (m) => ({ ...m, join_url: m.join_url || "", perks: (m.perks || []).join("\n"), description: m.is_sample ? "" : m.description, price: m.is_sample ? "" : m.price });
const toBody = (f) => ({ ...f, order: Number(f.order) || 0, perks: f.perks.split("\n").map((p) => p.trim()).filter(Boolean), is_sample: false });

const Editor = ({ initial, onSave, onCancel, onDelete }) => {
  const [f, setF] = useState(initial);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  return (
    <div className="space-y-4 bg-white p-6" data-testid="membership-editor">
      <Input placeholder="Tier name" value={f.name} onChange={set("name")} className="rounded-none" data-testid="m-name-input" />
      <div className="grid grid-cols-2 gap-3">
        <Input placeholder="Price (e.g. $299)" value={f.price} onChange={set("price")} className="rounded-none" data-testid="m-price-input" />
        <Input placeholder="Period (e.g. per month)" value={f.period} onChange={set("period")} className="rounded-none" data-testid="m-period-input" />
      </div>
      <Textarea placeholder="Short description" value={f.description} onChange={set("description")} className="rounded-none" data-testid="m-desc-input" />
      <Textarea placeholder={"Perks — one per line"} rows={5} value={f.perks} onChange={set("perks")} className="rounded-none" data-testid="m-perks-input" />
      <Input type="url" placeholder="Vagaro join link for this plan (https://...)" value={f.join_url} onChange={set("join_url")} className="rounded-none" data-testid="m-join-url-input" />
      <Input type="number" placeholder="Display order" value={f.order} onChange={set("order")} className="rounded-none" data-testid="m-order-input" />
      <div className="flex flex-wrap gap-3">
        <button onClick={() => onSave(f)} className="btn-ink px-6 py-3" data-testid="m-save-btn">Save</button>
        {onCancel && <button onClick={onCancel} className="btn-line px-6 py-3" data-testid="m-cancel-btn">Cancel</button>}
        {onDelete && <button onClick={onDelete} className="px-4 text-[11px] uppercase tracking-[0.2em] text-red-700" data-testid="m-delete-btn">Delete</button>}
      </div>
    </div>
  );
};

export const MembershipsTab = () => {
  const [tiers, setTiers] = useState([]);
  const [editing, setEditing] = useState(null);
  const load = useCallback(() => http.get("/memberships").then(({ data }) => setTiers(data)), []);
  useEffect(() => { load(); }, [load]);
  const save = async (id, f) => {
    try {
      if (id === "new") await http.post("/admin/memberships", toBody(f));
      else await http.put(`/admin/memberships/${id}`, toBody(f));
      toast.success("Membership saved — live on the website");
      setEditing(null);
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };
  const remove = async (id) => { await http.delete(`/admin/memberships/${id}`); toast.success("Deleted"); setEditing(null); load(); };
  return (
    <div className="space-y-6">
      <p className="text-sm font-light text-neutral-600">Tiers marked <strong>Sample</strong> show “Pricing coming soon” on the site until you edit them.</p>
      <div className="grid gap-6 md:grid-cols-3">
        {tiers.map((m) =>
          editing === m.id ? (
            <Editor key={m.id} initial={toForm(m)} onSave={(f) => save(m.id, f)} onCancel={() => setEditing(null)} onDelete={() => remove(m.id)} />
          ) : (
            <div key={m.id} className="bg-white p-6" data-testid={`admin-tier-${m.id}`}>
              {m.is_sample && <span className="text-[10px] uppercase tracking-[0.25em] text-amber-700">Sample</span>}
              <p className="font-display text-3xl">{m.name}</p>
              <p className="mt-1 text-sm">{m.price} {m.period}</p>
              <p className="mt-3 text-xs text-neutral-500">{m.perks?.length || 0} perks</p>
              <button onClick={() => setEditing(m.id)} className="link-under mt-5" data-testid={`admin-tier-edit-${m.id}`}>Edit</button>
            </div>
          )
        )}
        {editing === "new" ? (
          <Editor initial={EMPTY} onSave={(f) => save("new", f)} onCancel={() => setEditing(null)} />
        ) : (
          <button onClick={() => setEditing("new")} className="flex min-h-[180px] items-center justify-center border border-dashed border-neutral-300 text-[11px] uppercase tracking-[0.2em] text-neutral-600 hover:border-neutral-950" data-testid="admin-tier-add">+ Add Tier</button>
        )}
      </div>
    </div>
  );
};
